import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, Label, TextInput } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiPhotograph, HiTrash, HiEye, HiChartBar, HiRefresh, HiX } from 'react-icons/hi';
import { format } from 'date-fns';
import { screenStore } from '../stores/screenStore';
import { toastStore } from '../stores/toastStore';
import Loading from '../components/Loading';
import type { WaitingScreen } from '../types';

/** 元画像とクロップ済み画像を並べて表示する。クロップ済みが存在しない場合は元画像のみ表示。 */
const ScreenImagePair: React.FC<{ screen: WaitingScreen | null }> = ({ screen }) => {
  const [croppedFailed, setCroppedFailed] = useState(false);

  // screen が変わったらリセット
  useEffect(() => { setCroppedFailed(false); }, [screen?.id]);

  if (!screen) return null;

  const hasCropped = !!screen.croppedImageUrl && !croppedFailed;

  if (hasCropped) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">元画像</p>
          <img src={screen.imageUrl} alt="元画像" className="w-full rounded border border-gray-200" />
        </div>
        <div>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">クロップ済み（LLMへ渡した画像）</p>
          <img
            src={screen.croppedImageUrl}
            alt="クロップ済み画像"
            className="w-full rounded border border-blue-200"
            onError={() => setCroppedFailed(true)}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <img src={screen.imageUrl} alt="元画像" className="w-full rounded border border-gray-200" />
      {screen.croppedImageUrl && croppedFailed && (
        <p className="text-xs text-gray-400 mt-2 text-center">クロップ済み画像なし（クロップ未設定または失敗）</p>
      )}
    </div>
  );
};

const ScreensPage: React.FC = observer(() => {
  const [isLoading, setIsLoading] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<WaitingScreen | null>(null);

  const handleShowImage = (screen: WaitingScreen) => {
    setSelectedScreen(screen);
    setShowImageModal(true);
  };

  const handleShowAnalysis = (screen: WaitingScreen) => {
    setSelectedScreen(screen);
    setShowAnalysisModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await screenStore.fetchScreens();
      setIsLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (screenStore.error) {
      toastStore.showError(screenStore.error);
      screenStore.clearError();
    }
  }, [screenStore.error]);

  const handleDelete = async (id: string) => {
    if (window.confirm('この画像を削除しますか？')) {
      await screenStore.deleteScreen(id);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">画面履歴</h1>
          <p className="text-gray-600">キャプチャ画像の履歴とAI解析結果</p>
        </div>
        <Button onClick={() => screenStore.fetchScreens()}>
          <HiRefresh className="mr-2 h-4 w-4" />
          更新
        </Button>
      </div>

      {/* 日付フィルタ */}
      <Card>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="fromDate" value="開始日" />
            <TextInput
              id="fromDate"
              type="date"
              value={screenStore.filters.fromDate}
              onChange={(e) => screenStore.setFilter('fromDate', e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <Label htmlFor="fromTime" value="開始時刻" />
            <TextInput
              id="fromTime"
              type="time"
              value={screenStore.filters.fromTime}
              onChange={(e) => screenStore.setFilter('fromTime', e.target.value)}
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="toDate" value="終了日" />
            <TextInput
              id="toDate"
              type="date"
              value={screenStore.filters.toDate}
              onChange={(e) => screenStore.setFilter('toDate', e.target.value)}
              color={screenStore.dateFilterError ? 'failure' : undefined}
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <div>
              <Label htmlFor="toTime" value="終了時刻" />
              <TextInput
                id="toTime"
                type="time"
                value={screenStore.filters.toTime}
                onChange={(e) => screenStore.setFilter('toTime', e.target.value)}
                color={screenStore.dateFilterError ? 'failure' : undefined}
              />
              {screenStore.dateFilterError && (
                <p className="mt-1 text-sm text-red-600">
                  {screenStore.dateFilterError}
                </p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="toTime" value="クリア" className="opacity-0" />
            <Button
              id="clear"
              color="gray"
              onClick={() => screenStore.clearFilters()}
              disabled={!screenStore.filters.fromDate && !screenStore.filters.toDate && !screenStore.filters.fromTime && !screenStore.filters.toTime}
            >
              <HiX className="mr-2 h-4 w-4" />
              クリア
            </Button>
          </div>
        </div>
      </Card>

      {/* 画面一覧 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {screenStore.screens.map((screen) => (
          <Card key={screen.id}>
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
              <img
                src={screen.imageUrl}
                alt={`Screen ${screen.screenNumber}`}
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition"
                onClick={() => handleShowImage(screen)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">画面 {screen.screenNumber}</p>
                  <p className="text-sm text-gray-500">
                    {screen.createdAt
                      ? format(new Date(screen.createdAt), 'yyyy/MM/dd HH:mm:ss')
                      : '-'}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="xs"
                    color="gray"
                    onClick={() => handleShowImage(screen)}
                  >
                    <HiEye className="h-3 w-3" />
                  </Button>
                  <Button
                    size="xs"
                    color="failure"
                    onClick={() => handleDelete(screen.id)}
                  >
                    <HiTrash className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {/* AI解析結果 */}
              <div className="border-t pt-2">
                {screen.skippedReason ? (
                  <Button
                    size="sm"
                    color="gray"
                    className="w-full"
                    onClick={() => handleShowAnalysis(screen)}
                  >
                    <HiChartBar className="mr-2 h-4 w-4" />
                    解析スキップ
                  </Button>
                ) : screen.details && screen.details.length > 0 ? (
                  <Button
                    size="sm"
                    color="light"
                    className="w-full"
                    onClick={() => handleShowAnalysis(screen)}
                  >
                    <HiChartBar className="mr-2 h-4 w-4" />
                    解析結果を見る
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="light"
                    className="w-full"
                    disabled
                  >
                    <HiChartBar className="mr-2 h-4 w-4" />
                    解析中
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* 続きを取得ボタン */}
      {screenStore.hasMore && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={() => screenStore.fetchMoreScreens()}
            disabled={screenStore.isLoading}
            color="gray"
          >
            {screenStore.isLoading ? '読み込み中...' : '続きを取得'}
          </Button>
        </div>
      )}

      {/* 画像がない場合 */}
      {screenStore.screens.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <HiPhotograph className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">まだキャプチャ画像がありません</p>
            <p className="text-sm text-gray-400 mt-2">IoT端末からの自動アップロードを待っています</p>
          </div>
        </Card>
      )}

      {/* 画像表示モーダル */}
      <Modal show={showImageModal} onClose={() => setShowImageModal(false)} size="7xl">
        <Modal.Header>画像プレビュー - 画面 {selectedScreen?.screenNumber}</Modal.Header>
        <Modal.Body>
          <ScreenImagePair screen={selectedScreen} />
        </Modal.Body>
      </Modal>

      {/* 解析結果モーダル */}
      <Modal
        show={showAnalysisModal}
        onClose={() => {
          setShowAnalysisModal(false);
          setSelectedScreen(null);
        }}
        size="5xl"
      >
        <Modal.Header>
          {selectedScreen?.skippedReason ? 'LLM解析スキップ' : 'AI解析結果'} - 画面 {selectedScreen?.screenNumber}
        </Modal.Header>
        <Modal.Body>
          <div className="mb-6">
            <ScreenImagePair screen={selectedScreen} />
          </div>
          {selectedScreen?.details && selectedScreen.details.length > 0 ? (
            <div className="space-y-4">
              {selectedScreen.details.map((detail, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                    解析結果 {index + 1}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="font-medium text-gray-600 w-24">場所:</span>
                      <span className="text-gray-900">{detail.roomName || '-'}</span>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-gray-600 w-24">名前:</span>
                      <span className="text-gray-900">
                        {detail.callerName || '-'}
                      </span>
                    </div>
                    <div className="flex">
                      <span className="font-medium text-gray-600 w-24">呼出中:</span>
                      <span className="text-gray-900 font-bold text-lg">
                        {detail.callNumber || '-'}
                      </span>
                    </div>
                    {detail.waitingNumbers && detail.waitingNumbers.length > 0 && (
                      <div className="flex">
                        <span className="font-medium text-gray-600 w-32">呼び出し待機中:</span>
                        <span className="text-gray-900">
                          {detail.waitingNumbers.join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* LLM使用量 */}
              {selectedScreen?.inputTokens !== undefined && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">LLM使用量</h4>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    {selectedScreen.llmProvider && (
                      <>
                        <dt className="text-gray-500">プロバイダー</dt>
                        <dd className="text-gray-900">{selectedScreen.llmProvider}</dd>
                      </>
                    )}
                    {selectedScreen.llmModel && (
                      <>
                        <dt className="text-gray-500">モデル</dt>
                        <dd className="text-gray-900">{selectedScreen.llmModel}</dd>
                      </>
                    )}
                    <dt className="text-gray-500">入力トークン</dt>
                    <dd className="text-gray-900">{selectedScreen.inputTokens.toLocaleString()}</dd>
                    <dt className="text-gray-500">出力トークン</dt>
                    <dd className="text-gray-900">{(selectedScreen.outputTokens ?? 0).toLocaleString()}</dd>
                  </dl>
                </div>
              )}

              {selectedScreen.createdAt && (
                <div className="text-sm text-gray-500 text-right pt-2 border-t">
                  アップロード日時: {format(new Date(selectedScreen.createdAt), 'yyyy/MM/dd HH:mm:ss')}
                </div>
              )}
              {selectedScreen.updatedAt && selectedScreen.details?.length > 0 && (
                <div className="text-sm text-gray-500 text-right pt-2">
                  解析完了日時: {format(new Date(selectedScreen.updatedAt), 'yyyy/MM/dd HH:mm:ss')}
                </div>
              )}
            </div>
          ) : selectedScreen?.skippedReason === 'no_uncalled_guests' ? (
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
              <p className="text-yellow-800 font-medium">LLM解析をスキップしました</p>
              <p className="text-yellow-700 text-sm mt-1">
                画像アップロード時点で当日の未呼び出しゲストが存在しなかったため、LLM解析は実行されませんでした。
              </p>
            </div>
          ) : (
            <p className="text-gray-500">解析結果がありません（解析中）</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="gray"
            onClick={() => {
              setShowAnalysisModal(false);
              setSelectedScreen(null);
            }}
          >
            閉じる
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ScreensPage;